import { Button } from '@mui/material';
import PrivateRoute from 'components/Auth/PrivateRoute';
import StudyDashboard from 'components/StudyDashboard';
import { isEmpty, keys } from 'es-toolkit/compat';
import {
  globalSettingsEditingPermissions,
  globalSettingsViewingPermissions,
  Permission,
} from 'interfaces/permissionOption';
import React, { useEffect, useSyncExternalStore } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import AuthenticationErrorScreen from 'components/Auth/AuthenticationErrorScreen';
import AuthService, { Auth0Profile } from 'utils/auth/AuthService';
import { LOCAL_WORK_BACKUP_FAILED_REASON } from 'utils/auth/authStoragePrune';
import sessionCoordinator from 'utils/auth/sessionCoordinator';
import { AUTH_SESSION_STATUS } from 'utils/auth/sessionTypes';
import { useSetInternalUserId } from 'utils/auth/useSetInternalUserId';
import { useSetInitialLabId } from 'utils/useSetInitialLabId';
import { useSetInitialStudyId } from 'utils/useSetInitialStudyId';
import './App.scss';
import AppShellLoader from './AppShellLoader';
import CookieNotice from './CookieNotice';
import ErrorSnackbar from './ErrorSnackbar';
import { LoadingStage } from './Loader';
import LostAndFoundPage from './LostAndFound';
import MainPageRoute from './MainPage';
import NotFound from './NotFound';
import { CopilotAdmin } from './Pages/CopilotAdmin';
import { ManageLabs } from './Pages/ManageLabs';
import NucleaiOriginsPage from './Pages/NucleaiOrigins';
import { NUCLEAI_ORIGINS_ROUTE_PATH } from './Pages/NucleaiOrigins/navigation';
import { OperationsSettings } from './Pages/OperationsSettings';
import StudyReportRoutes from './Pages/StudyReport';
import { REPORT_CANVAS_ROUTE_PATH } from './Pages/StudyReport/studyReportRouteNavigation';
import { UserCredentialsRolesAndPermissions } from './Pages/UserCredentialsManagement';
import PrivacyPolicy from './Pages/static/PrivacyPolicy';
import TermsAndConditions from './Pages/static/TermsAndConditions';
import { useUploadFilesContext } from './UploadFilesContext';
import UploadProcess from './UploadProcess/UploadProcess';
import UploadsDashboard from './UploadsDashboard';

const ProcedurePage = React.lazy(() => import('components/Procedure/ProcedurePage'));
const SlidePage = React.lazy(() =>
  import('components/Procedure/SlidePage').then((module) => ({ default: module.SlidePage }))
);

const withRouteSuspense = (Component: React.ComponentType<any>, stage: LoadingStage) => {
  const SuspendedRouteComponent: React.FC<any> = (routeProps) => (
    <React.Suspense fallback={<AppShellLoader stage={stage} />}>
      <Component {...routeProps} />
    </React.Suspense>
  );

  return SuspendedRouteComponent;
};

const ProcedurePageRoute = withRouteSuspense(ProcedurePage, LoadingStage.LoadingCase);
const SlidePageRoute = withRouteSuspense(SlidePage, LoadingStage.LoadingSlide);

const getAuthenticationErrorPresentation = (
  authenticationReason: string | null,
  retryIntent: 'signIn' | 'logout' | null
) => {
  if (authenticationReason !== LOCAL_WORK_BACKUP_FAILED_REASON) {
    return {
      actionLabel: 'Try Again',
      message: 'Sign-in could not be completed.',
      title: 'Sign-in could not be completed',
    };
  }

  if (retryIntent === 'logout') {
    return {
      actionLabel: 'Retry Log Out',
      message: 'Changes on this page could not be safely preserved, so you were not signed out.',
      title: 'This page is still open',
    };
  }

  return {
    actionLabel: 'Retry Sign-In',
    message: 'Changes on this page could not be safely preserved. This page is still open.',
    title: 'This page is still open',
  };
};

interface Props {
  hasError: boolean;
  errorMessage?: string;
  isAuthenticated: boolean;
  isLoggingOut: boolean;
  triedAuthenticateFromLocalStorage: boolean;
  userProfile: Auth0Profile;
  tryAuthenticateFromLocalStorage: (currentProfile?: Auth0Profile) => void;
}

const AppRouter: React.FunctionComponent<React.PropsWithChildren<Props>> = (props) => {
  const isAuthenticated = props.isAuthenticated;

  useSetInitialLabId();
  useSetInternalUserId();
  useSetInitialStudyId();

  return (
    <Routes>
      <Route
        path="/terms-and-conditions"
        element={<PrivateRoute component={TermsAndConditions} isAuthenticated={isAuthenticated} />}
      />
      <Route
        path="/privacy-policy"
        element={<PrivateRoute component={PrivacyPolicy} isAuthenticated={isAuthenticated} />}
      />
      <Route
        path="/uploads-dashboard"
        element={<PrivateRoute component={UploadsDashboard} isAuthenticated={isAuthenticated} />}
      />
      <Route
        path="/lost-and-found"
        element={
          <PrivateRoute
            component={LostAndFoundPage}
            permissionsRequired={[Permission.ViewPendingSlides]}
            isAuthenticated={isAuthenticated}
          />
        }
      />

      <Route
        path={`${REPORT_CANVAS_ROUTE_PATH}/*`}
        element={
          <PrivateRoute
            component={StudyReportRoutes}
            permissionsRequired={[Permission.AccessAnalysisStudio, Permission.ViewNucleaiCopilot]}
            isAuthenticated={isAuthenticated}
          />
        }
      />

      <Route
        path={NUCLEAI_ORIGINS_ROUTE_PATH}
        element={
          <PrivateRoute
            component={NucleaiOriginsPage}
            permissionsRequired={[Permission.ApplyPermissionsAcrossLabs]}
            isAuthenticated={isAuthenticated}
          />
        }
      />

      <Route
        path="/admin/user-credentials"
        element={
          <PrivateRoute
            component={UserCredentialsRolesAndPermissions}
            permissionsRequired={[Permission.DefineRolesToLabUsers, Permission.ManageNucleaiUsers]}
            isAuthenticated={isAuthenticated}
          />
        }
      />
      <Route
        path="/admin/operations-settings"
        element={
          <PrivateRoute
            component={OperationsSettings}
            permissionsRequired={[...globalSettingsEditingPermissions, ...globalSettingsViewingPermissions]}
            isAuthenticated={isAuthenticated}
          />
        }
      />
      <Route
        path="/admin/copilot-usage"
        element={
          <PrivateRoute
            component={CopilotAdmin}
            permissionsRequired={[Permission.ViewCopilotTokenUsage]}
            isAuthenticated={isAuthenticated}
          />
        }
      />
      <Route
        path="/admin/create-lab"
        element={
          <PrivateRoute
            component={ManageLabs}
            permissionsRequired={[Permission.CreateLab, Permission.EditLab, Permission.DeleteLab]}
            isAuthenticated={isAuthenticated}
          />
        }
      />
      <Route
        path="/admin/manage-labs"
        element={
          <PrivateRoute
            component={ManageLabs}
            permissionsRequired={[Permission.CreateLab, Permission.EditLab, Permission.DeleteLab]}
            isAuthenticated={isAuthenticated}
          />
        }
      />
      <Route
        path="/procedures"
        element={<PrivateRoute component={StudyDashboard} isAuthenticated={isAuthenticated} />}
      />

      <Route path="/" element={<MainPageRoute isAuthenticated={isAuthenticated} />} />

      <Route
        path="/procedures/:id"
        element={<PrivateRoute component={ProcedurePageRoute} isAuthenticated={isAuthenticated} />}
      />

      <Route
        path="/slides/:slideId"
        element={<PrivateRoute component={SlidePageRoute} isAuthenticated={isAuthenticated} />}
      />

      <Route
        path="/study/:studyId/procedures/:id"
        element={<PrivateRoute component={ProcedurePageRoute} isAuthenticated={isAuthenticated} />}
      />

      <Route
        path="/study/:studyId/slides/:slideId"
        element={<PrivateRoute component={SlidePageRoute} isAuthenticated={isAuthenticated} />}
      />

      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const AppView: React.FunctionComponent<React.PropsWithChildren<Props>> = (props) => {
  const { isAuthenticated } = props;
  const sessionSnapshot = useSyncExternalStore(
    (listener) => sessionCoordinator.subscribe(listener),
    () => sessionCoordinator.getSessionSnapshot(),
    () => sessionCoordinator.getSessionSnapshot()
  );
  const { authenticationReason, retryIntent, sessionStatus } = sessionSnapshot;
  const authenticationErrorPresentation = getAuthenticationErrorPresentation(authenticationReason, retryIntent);
  const shouldHoldAutomaticLogin =
    sessionStatus === AUTH_SESSION_STATUS.authenticationError ||
    sessionStatus === AUTH_SESSION_STATUS.redirectingToLogin ||
    sessionStatus === AUTH_SESSION_STATUS.reauthenticationRequired ||
    sessionStatus === AUTH_SESSION_STATUS.loggingOut;

  const { fileUploads, uploadsCompleted, isPending } = useUploadFilesContext();

  const uploadSnackbarVisible = !isEmpty(keys(fileUploads));

  useEffect(() => {
    if (isPending || (!isEmpty(fileUploads) && !uploadsCompleted)) {
      window.addEventListener('beforeunload', handleBeforeUnload);
    } else {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    }
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [uploadsCompleted, isEmpty(fileUploads), isPending]);

  const handleBeforeUnload = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
  };

  useEffect(() => {
    props.tryAuthenticateFromLocalStorage(props.userProfile);
  }, []);

  useEffect(() => {
    if (isAuthenticated || !props.triedAuthenticateFromLocalStorage || props.isLoggingOut || shouldHoldAutomaticLogin) {
      return;
    }

    if (AuthService.isPkceCallback()) {
      return;
    }

    void AuthService.login();
  }, [isAuthenticated, props.isLoggingOut, props.triedAuthenticateFromLocalStorage, shouldHoldAutomaticLogin]);

  return (
    <>
      {uploadSnackbarVisible && <UploadProcess />}

      {props.hasError && (
        <ErrorSnackbar
          message={props.errorMessage}
          action={
            // TODO: once we have a better error handling, we should do a proper retry instead of a reload: https://nucleai.atlassian.net/browse/ATOM-1034
            <Button sx={{ color: 'white' }} onClick={() => window.location.reload()}>
              Try again
            </Button>
          }
        />
      )}
      <HashRouter>
        <QueryParamProvider adapter={ReactRouter6Adapter}>
          <CookieNotice />
          {sessionStatus === AUTH_SESSION_STATUS.authenticationError && !isAuthenticated ? (
            <AuthenticationErrorScreen {...authenticationErrorPresentation} />
          ) : !isAuthenticated && !props.triedAuthenticateFromLocalStorage ? (
            <AppShellLoader stage={props.isLoggingOut ? LoadingStage.SigningOut : LoadingStage.SigningIn} />
          ) : (
            <>
              {sessionStatus === AUTH_SESSION_STATUS.authenticationError && (
                <ErrorSnackbar
                  message={authenticationErrorPresentation.message}
                  action={
                    <Button sx={{ color: 'white' }} onClick={() => void sessionCoordinator.retryAuthentication()}>
                      {authenticationErrorPresentation.actionLabel}
                    </Button>
                  }
                />
              )}
              <AppRouter {...props} />
            </>
          )}
        </QueryParamProvider>
      </HashRouter>
    </>
  );
};

// eslint-disable-next-line no-undef
export default AppView;
